import type { AuthUser } from '@/lib/auth/store';

const LOCAL_DEV_TOKEN_TTL_SECONDS = 8 * 60 * 60; // long-lived — local-only, no refresh loop needed

interface LocalDevTokenResult {
  token: string;
  /** epoch ms */
  expiresAt: number;
  user: AuthUser;
}

// Client-side stand-in for functions/api/auth/token.ts — dev-only (import.meta.env.DEV
// is dead-code eliminated in real builds), opt-in via .env.development.local. Puts
// BOP_API_JWT_SECRET in the browser bundle for local dev; don't extend this pattern elsewhere.
export const mintLocalDevToken = async (): Promise<LocalDevTokenResult | null> => {
  if (!import.meta.env.DEV) return null;

  const secret = import.meta.env.VITE_LOCAL_DEV_JWT_SECRET;
  const email = import.meta.env.VITE_LOCAL_DEV_EMAIL;
  if (!secret || !email) return null;

  // An unfilled `<...>` placeholder copied from the README signs a structurally valid token
  // that bop-api rejects — the app looks signed in and 401s on every call. Fail here instead.
  if (/^<.*>$/.test(secret)) {
    throw new Error('VITE_LOCAL_DEV_JWT_SECRET in .env.development.local is still a placeholder — set it to the real BOP_API_JWT_SECRET.');
  }

  const name = import.meta.env.VITE_LOCAL_DEV_NAME || email;
  const expiresAt = Date.now() + LOCAL_DEV_TOKEN_TTL_SECONDS * 1000;

  // Dynamic import keeps jose out of real (non-DEV) bundles entirely.
  const { SignJWT } = await import('jose');
  const token = await new SignJWT({ sub: email, name, email })
    .setProtectedHeader({ alg: 'HS256' })
    .setIssuedAt()
    .setExpirationTime(Math.floor(expiresAt / 1000))
    .sign(new TextEncoder().encode(secret));

  return { token, expiresAt, user: { name, email } };
};
